import useSWR, { type SWRConfiguration } from 'swr';
import {
  getBalanceDetails,
  getTotalOnChainBalance,
  type TokenBalanceDetail,
} from '@/lib/predic/onchain/onChainBalance';

export interface OnChainWalletBalance {
  wallet: string;
  balance: number;
  details: TokenBalanceDetail[];
}

export interface OnChainBalanceData {
  totalBalance: number;
  wallets: OnChainWalletBalance[];
  balanceByWallet: Record<string, number>;
  detailsByWallet: Record<string, TokenBalanceDetail[]>;
}

export interface FetchOnChainBalanceParams {
  wallet?: string | null;
  wallets?: string[] | null;
  includeDetails?: boolean;
}

export interface UseOnChainBalanceOptions extends FetchOnChainBalanceParams {
  enabled?: boolean;
  swr?: SWRConfiguration<OnChainBalanceData, Error>;
}

export async function fetchOnChainBalance({
  wallet,
  wallets,
  includeDetails = true,
}: FetchOnChainBalanceParams): Promise<OnChainBalanceData> {
  const normalizedWallets = normalizeWallets(wallet ? [wallet] : (wallets ?? []));

  if (normalizedWallets.length === 0) {
    return {
      totalBalance: 0,
      wallets: [],
      balanceByWallet: {},
      detailsByWallet: {},
    };
  }

  const walletBalances = await Promise.all(
    normalizedWallets.map(async (walletAddress) => {
      const [balance, details] = await Promise.all([
        getTotalOnChainBalance(walletAddress),
        includeDetails ? getBalanceDetails(walletAddress) : Promise.resolve([]),
      ]);

      return {
        wallet: walletAddress,
        balance,
        details,
      };
    }),
  );

  const totalBalance = walletBalances.reduce((sum, item) => sum + item.balance, 0);
  const balanceByWallet = Object.fromEntries(
    walletBalances.map((item) => [item.wallet, item.balance]),
  );
  const detailsByWallet = Object.fromEntries(
    walletBalances.map((item) => [item.wallet, item.details]),
  );

  return {
    totalBalance,
    wallets: walletBalances,
    balanceByWallet,
    detailsByWallet,
  };
}

export function useOnChainBalance(options: UseOnChainBalanceOptions = {}) {
  const wallets = normalizeWallets(options.wallet ? [options.wallet] : (options.wallets ?? []));
  const includeDetails = options.includeDetails ?? true;
  const enabled = options.enabled ?? true;
  const swrKey =
    enabled && wallets.length > 0
      ? (['onchain-balance', wallets.join(','), includeDetails] as const)
      : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<OnChainBalanceData, Error>(
    swrKey,
    () => fetchOnChainBalance({ wallets, includeDetails }),
    {
      revalidateOnFocus: false,
      dedupingInterval: 30000,
      ...options.swr,
    },
  );

  return {
    data: data ?? null,
    totalBalance: data?.totalBalance ?? 0,
    wallets: data?.wallets ?? [],
    balanceByWallet: data?.balanceByWallet ?? {},
    detailsByWallet: data?.detailsByWallet ?? {},
    error,
    isLoading,
    isValidating,
    refresh: mutate,
  };
}

function normalizeWallets(wallets: string[]) {
  return [...new Set(wallets.map((wallet) => wallet.trim().toLowerCase()).filter(Boolean))];
}

export default useOnChainBalance;
